import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import api, { jobsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Zap,
  Search,
  Briefcase,
  Loader2,
  ChevronRight,
  Award,
  Phone,
  Mail,
  MapPin,
  Clock,
  DollarSign,
  Target,
  TrendingUp,
  Users,
  Building2,
  Receipt,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

const formatSalary = (salary) => {
  if (!salary) return 'N/A';
  const s = String(salary).toLowerCase();
  if (s.includes('lpa') || s.includes('lakh')) return salary;
  const num = parseFloat(salary);
  if (isNaN(num)) return salary;
  if (num >= 100000) return `₹${(num/100000).toFixed(1)} LPA`;
  return `₹${num.toLocaleString()}`;
};

const formatMargin = (margin) => {
  if (!margin || margin === 0) return 'N/A';
  if (margin >= 100000) return `₹${(margin/100000).toFixed(1)}L`;
  return `₹${Math.round(margin).toLocaleString()}`;
};

export default function DealCloserPage() {
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [expandedCandidate, setExpandedCandidate] = useState(null);
  const [jobSearch, setJobSearch] = useState('');
  // Recorded placements for the selected job (Phase 5 `placements`, via routes/placements_v2.py).
  // Real money, beside the estimate: signed CTC, fee, basis, signed/provisional, invoice position.
  const [recorded, setRecorded] = useState(null);

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (selectedJobId) {
      loadDealCloser(selectedJobId);
    }
  }, [selectedJobId]);

  const loadJobs = async () => {
    try {
      const response = await jobsApi.list({ limit: 100, status: 'open' });
      setJobs(response.data.jobs || []);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadDealCloser = async (jobId) => {
    setCandidatesLoading(true);
    setExpandedCandidate(null);
    setRecorded(null);
    api.get('/placements/v2/list', { params: { job_id: jobId, limit: 50 } })
      .then((res) => setRecorded(res.data))
      .catch(() => setRecorded(null));   // module not installed - card stays hidden
    try {
      const response = await jobsApi.getDealCloser(jobId, { limit: 10 });
      setCandidates([...(response.data.top_candidates || [])]
          // The API ranks by its own blend of match, placement odds and margin,
          // which let a poor fit sit above a good one. The page promises match
          // score first, so order by that.
          .sort((a, b) => (b.match_score || 0) - (a.match_score || 0)));
      setSelectedJob({
        id: jobId,
        title: response.data.job_title,
        company: response.data.company_name,
        totalScanned: response.data.total_candidates_scanned,
        matchesFound: response.data.matches_found,
      });
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    !jobSearch || 
    job.job_title?.toLowerCase().includes(jobSearch.toLowerCase()) ||
    job.company_name?.toLowerCase().includes(jobSearch.toLowerCase())
  );

  return (
    <div>
      <Header title="Deal Closer" subtitle="Find top candidates for any job instantly">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg text-sm">
          <Zap className="w-4 h-4" />
          AI-Powered Ranking
        </div>
      </Header>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Job Selector */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-600" />
                  Select a Job
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search jobs..."
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Job List */}
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="max-h-[60vh] overflow-y-auto space-y-2">
                    {filteredJobs.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">No open jobs found</p>
                    ) : (
                      filteredJobs.map(job => (
                        <div
                          key={job.id}
                          onClick={() => setSelectedJobId(job.id)}
                          className={`p-3 rounded-lg cursor-pointer transition-all border ${
                            selectedJobId === job.id
                              ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                              : 'border-slate-200 hover:border-purple-300 hover:bg-slate-50'
                          }`}
                          data-testid={`job-select-${job.id}`}
                        >
                          <p className="font-medium text-slate-900 text-sm truncate">{job.job_title}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {job.company_name || 'No Company'} • {job.primary_location || 'Remote'}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">
                              {job.number_of_openings || 1} opening{(job.number_of_openings || 1) > 1 ? 's' : ''}
                            </span>
                            {job.billing_rate_percentage && (
                              <span className="text-xs text-emerald-600 font-medium">
                                {job.billing_rate_percentage}%
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Top Candidates */}
          <div className="lg:col-span-2">
            {!selectedJobId ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700">Select a Job</h3>
                  <p className="text-slate-500 mt-2 max-w-md mx-auto">
                    Choose a job from the list to see the top 10 candidates ranked by match score, placement probability, and revenue potential.
                  </p>
                </CardContent>
              </Card>
            ) : candidatesLoading ? (
              <Card>
                <CardContent className="py-20 text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-500 mx-auto mb-4" />
                  <p className="text-slate-600">Analyzing candidates...</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Job Summary Header */}
                <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Top Candidates for</p>
                        <h2 className="text-xl font-bold">{selectedJob?.title}</h2>
                        <p className="text-purple-200 text-sm">{selectedJob?.company || 'No Company'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold">{candidates.length}</p>
                        <p className="text-purple-200 text-sm">matches found</p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {selectedJob?.totalScanned} scanned
                      </span>
                      <Link 
                        to={`/ats/jobs/${selectedJobId}`}
                        className="flex items-center gap-1 hover:underline"
                      >
                        View Job <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Recorded placements for this job - from `placements`, not estimated */}
                {recorded && recorded.placements && recorded.placements.length > 0 && (
                  <Card className="border-emerald-200" data-testid="deal-closer-recorded-placements">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-emerald-600" />
                        Recorded placements for this job
                        <span className="text-xs font-normal text-slate-500">
                          {recorded.total} · fee {formatMargin(recorded.total_fee_amount)}
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="divide-y">
                        {recorded.placements.map((p) => (
                          <div key={p.id} className="py-2 flex items-center gap-3 flex-wrap text-sm">
                            <Link
                              to={`/ats/candidates/${p.candidate_id}`}
                              className="font-medium text-slate-800 hover:text-emerald-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {p.candidate_name || p.candidate_id}
                            </Link>
                            <span className="text-slate-600">CTC {formatMargin(p.signed_annual_ctc)}</span>
                            <span className="text-emerald-700 font-medium">fee {formatMargin(p.fee_amount)}{p.fee_percentage ? ` (${p.fee_percentage}%)` : ''}</span>
                            <span className="text-xs text-slate-500" title={p.fee_basis}>{p.fee_basis}</span>
                            {p.ctc_confirmed ? (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />signed CTC</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" />provisional CTC</span>
                            )}
                            {p.joining_date && <span className="text-xs text-slate-500">joined {String(p.joining_date).slice(0, 10)}</span>}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              p.invoice_status === 'paid' ? 'bg-green-100 text-green-700' :
                              p.invoice_status === 'overdue' ? 'bg-red-100 text-red-700' :
                              p.invoice_status === 'due_soon' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {p.invoice_status === 'paid' ? 'paid' :
                               p.invoice_status === 'overdue' ? `invoice overdue ${p.days_overdue}d` :
                               p.invoice_status === 'due_soon' ? `invoice due in ${p.days_to_due}d` :
                               p.invoice_due_date ? `due ${p.invoice_due_date}` : 'no joining date'}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2">
                        Ranking pills below are estimates (job billing rate or 8.33%); edit recorded figures in Hired Candidates.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Candidates List */}
                {candidates.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600">No matching candidates found</p>
                      <p className="text-sm text-slate-400">Try adjusting job requirements</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {candidates.map((candidate, index) => (
                      <Card 
                        key={candidate.candidate_id}
                        className={`transition-all cursor-pointer ${
                          expandedCandidate === index ? 'ring-2 ring-purple-500' : 'hover:shadow-md'
                        }`}
                        onClick={() => setExpandedCandidate(expandedCandidate === index ? null : index)}
                        data-testid={`candidate-card-${index}`}
                      >
                        <CardContent className="p-4">
                          {/* Main Row */}
                          <div className="flex items-center gap-4">
                            {/* Rank */}
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                              index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                              index === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                              index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-600 text-white' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              #{index + 1}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-900">{candidate.candidate_name}</p>
                              <p className="text-sm text-slate-500 truncate">
                                {candidate.current_designation || 'N/A'} 
                                {candidate.current_company && ` at ${candidate.current_company}`}
                              </p>
                            </div>

                            {/* Scores */}
                            <div className="flex items-center gap-3">
                              <div className="text-center">
                                <p className="text-lg font-bold text-purple-600">{candidate.ranking_score}%</p>
                                <p className="text-[10px] text-slate-400">SCORE</p>
                              </div>
                              <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedCandidate === index ? 'rotate-90' : ''}`} />
                            </div>
                          </div>

                          {/* Score Pills */}
                          <div className="flex gap-2 mt-3">
                            <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                              {candidate.match_score}% Match
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                              {candidate.placement_probability}% Probability
                            </span>
                            <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                              {formatMargin(candidate.margin_potential)} est. margin
                            </span>
                          </div>

                          {/* Expanded Details */}
                          {expandedCandidate === index && (
                            <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                              {/* Contact & Actions */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 text-sm">
                                  {candidate.email && (
                                    <a 
                                      href={`mailto:${candidate.email}`} 
                                      className="flex items-center gap-1 text-slate-600 hover:text-purple-600"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Mail className="w-4 h-4" />
                                      Email
                                    </a>
                                  )}
                                  {candidate.phone && (
                                    <a 
                                      href={`tel:${candidate.phone}`} 
                                      className="flex items-center gap-1 text-slate-600 hover:text-purple-600"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Phone className="w-4 h-4" />
                                      Call
                                    </a>
                                  )}
                                </div>
                                <Link 
                                  to={`/ats/candidates/${candidate.candidate_id}`}
                                  className="text-sm font-medium text-purple-600 hover:text-purple-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View Full Profile →
                                </Link>
                              </div>

                              {/* Details Grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-slate-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Experience</p>
                                    <p className="font-medium">{candidate.total_years_experience || 'N/A'} yrs</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-slate-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Location</p>
                                    <p className="font-medium truncate max-w-[120px]">{candidate.current_location || 'N/A'}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-4 h-4 text-slate-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Expected</p>
                                    <p className="font-medium">{formatSalary(candidate.expected_salary)}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-slate-400" />
                                  <div>
                                    <p className="text-slate-500 text-xs">Notice</p>
                                    <p className="font-medium">{candidate.notice_period_days || 'N/A'} days</p>
                                  </div>
                                </div>
                              </div>

                              {/* Score Breakdown */}
                              <div>
                                <p className="text-xs font-medium text-slate-500 mb-2">SCORE BREAKDOWN</p>
                                <div className="grid grid-cols-4 gap-2">
                                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                                    <p className="text-lg font-bold text-slate-700">{candidate.skills_score}%</p>
                                    <p className="text-xs text-slate-500">Skills</p>
                                  </div>
                                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                                    <p className="text-lg font-bold text-slate-700">{candidate.experience_score}%</p>
                                    <p className="text-xs text-slate-500">Experience</p>
                                  </div>
                                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                                    <p className="text-lg font-bold text-slate-700">{candidate.location_score}%</p>
                                    <p className="text-xs text-slate-500">Location</p>
                                  </div>
                                  <div className="text-center p-2 bg-slate-50 rounded-lg">
                                    <p className="text-lg font-bold text-slate-700">{candidate.compensation_score}%</p>
                                    <p className="text-xs text-slate-500">Compensation</p>
                                  </div>
                                </div>
                              </div>

                              {/* Matching Skills */}
                              {candidate.matching_skills?.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-2">MATCHING SKILLS</p>
                                  <div className="flex flex-wrap gap-1">
                                    {candidate.matching_skills.map((skill, i) => (
                                      <span key={i} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded">
                                        {skill}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Why Good Fit */}
                              {candidate.positive_factors?.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-2">WHY GOOD FIT</p>
                                  <div className="flex flex-wrap gap-2">
                                    {candidate.positive_factors.map((factor, i) => (
                                      <span key={i} className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded">
                                        <Award className="w-3 h-3" />
                                        {factor}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
